import { useTranslation } from 'next-i18next/pages'
import styles from './ProducerContact.module.scss'
import parse from 'html-react-parser'
import { BREAKPOINTS, CallToAction, Cards, Container, Header, Heading, Toggle } from '@boltenergy-be/design-system'
import { ProducerContactProps } from 'features/producer-contact/types'
import Link from 'components/link/Link'
import useWindowSize from 'hooks/useWindowSize'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import B2bOfferingCard from 'features/producer-contact/components/b2b-offering-card/B2bOfferingCard'
import { useState } from 'react'
import { routes } from 'utils/routes'
import { useGetLanguage } from 'hooks/useGetLanguage'

const ProducerContact = ({ faqCategories }: ProducerContactProps) => {
  // i18n
  const { t } = useTranslation('b2b', { keyPrefix: 'overview' })
  const language = useGetLanguage()

  // Check window size
  const { isSmaller } = useWindowSize(BREAKPOINTS.desktop)

  // Local state
  const [showMobileHighInjection, setShowMobileHighInjection] = useState<boolean>(false)

  return (
    <main>
      <Header as="section" color="dark" shape="s2" title={t('hero.title')} docked>
        <p>{t('hero.description')}</p>
      </Header>

      {/* OFFERING */}
      <Container as="section" classnames={{ content: styles['offering-content'] }}>
        <header className={styles.header}>
          <Heading as="h2" variant="d3">
            {t('offering.title')}
          </Heading>
          <p>{t('offering.description')}</p>
        </header>

        {isSmaller ? (
          <div data-offset={showMobileHighInjection} className={styles['cards-wrapper']}>
            <Toggle
              isFullwidth
              active={showMobileHighInjection}
              onClick={(value) => setShowMobileHighInjection(value)}
              options={[
                { label: t('offering.toggle.low'), value: false },
                { label: t('offering.toggle.high'), value: true }
              ]}
              className={styles.toggle}
            />

            {showMobileHighInjection ? (
              <div className={styles['scrollable-pro-cards']}>
                <B2bOfferingCard product="pro" className={styles.pro} />
                <B2bOfferingCard product="flex" className={styles.flex} />
              </div>
            ) : (
              <B2bOfferingCard product="basic" className={styles.basic} />
            )}
          </div>
        ) : (
          <div className={styles['cards-wrapper']}>
            <B2bOfferingCard product="basic" className={styles.basic} />

            <div className={styles['pro-cards']}>
              <strong className={styles.context}>{t('offering.proContext')}</strong>
              <B2bOfferingCard product="pro" className={styles.pro} />
              <B2bOfferingCard product="flex" className={styles.flex} />
            </div>
          </div>
        )}
      </Container>

      {/* WHY */}
      <Cards title={t('why.title')} description={t('why.description')}>
        {(t('why.cards', { returnObjects: true }) as { title: string; description: string }[]).map((action) => (
          <Cards.Card key={action.title} title={action.title} titleVariant="h4">
            <p>{parse(action.description)}</p>
          </Cards.Card>
        ))}
      </Cards>

      {/* CTA */}
      <CallToAction
        title={t('callToAction.title')}
        color="dark"
        shape="s6"
        action={
          <Link as="a" href={routes(language).becomeProducerForm} representation="button" trailingIcon="chevronRight">
            {t('callToAction.action')}
          </Link>
        }
      >
        <p>{t('callToAction.description')}</p>
      </CallToAction>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faqCategories} title={t('faq.title')} />
    </main>
  )
}

export default ProducerContact
